'use client'

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

export interface IntentShortcut {
    supplierCode: string
    vendorName: string
    fieldsSignature: string
    intentCategory: string
    intentNote: string | null
    createdAt: number
}

interface IntentShortcutContextValue {
    shortcuts: IntentShortcut[]
    addShortcut: (shortcut: Omit<IntentShortcut, 'createdAt'>) => void
    removeShortcut: (supplierCode: string, fieldsSignature: string) => void
    findShortcut: (supplierCode: string | null | undefined, fieldsSignature: string) => IntentShortcut | null
    clearAll: () => void
}

const IntentShortcutContext = createContext<IntentShortcutContextValue | null>(null)

/**
 * Sorted, stable signature of fields changed in a save. Used as the key for a
 * session shortcut so a clerk editing N invoices for the same vendor, changing
 * the same fields, only answers the reason modal once.
 */
export function computeFieldsSignature(fields: string[]): string {
    return [...fields].filter(Boolean).sort().join('|')
}

export function IntentShortcutProvider({ children }: { children: ReactNode }) {
    const [shortcuts, setShortcuts] = useState<IntentShortcut[]>([])

    const addShortcut = useCallback((input: Omit<IntentShortcut, 'createdAt'>) => {
        setShortcuts((current) => {
            const next = current.filter(
                (s) => !(s.supplierCode === input.supplierCode && s.fieldsSignature === input.fieldsSignature)
            )
            next.push({ ...input, createdAt: Date.now() })
            return next
        })
    }, [])

    const removeShortcut = useCallback((supplierCode: string, fieldsSignature: string) => {
        setShortcuts((current) =>
            current.filter((s) => !(s.supplierCode === supplierCode && s.fieldsSignature === fieldsSignature))
        )
    }, [])

    const findShortcut = useCallback(
        (supplierCode: string | null | undefined, fieldsSignature: string) => {
            if (!supplierCode) return null
            return shortcuts.find((s) => s.supplierCode === supplierCode && s.fieldsSignature === fieldsSignature) ?? null
        },
        [shortcuts],
    )

    const clearAll = useCallback(() => setShortcuts([]), [])

    const value = useMemo(
        () => ({ shortcuts, addShortcut, removeShortcut, findShortcut, clearAll }),
        [shortcuts, addShortcut, removeShortcut, findShortcut, clearAll],
    )

    return <IntentShortcutContext.Provider value={value}>{children}</IntentShortcutContext.Provider>
}

export function useIntentShortcut(): IntentShortcutContextValue {
    const ctx = useContext(IntentShortcutContext)
    if (!ctx) {
        throw new Error('useIntentShortcut must be used inside IntentShortcutProvider')
    }
    return ctx
}
